import { useState, useEffect, useContext, useCallback } from 'react';
import { validateEmail, validateNumber, validatePhone, validateUrl } from '../utils/validation';
import { getCountryCodeByRegion } from '../utils/countryCodeUtils';
import { AuthContext } from '../context/AuthContext';
import { checkRelevance, interpolateLabel } from '../utils/calculationEngine';

export const useFormValidation = (formValues, survey, nameValues = {}, activeRepeats = {}) => {
  const { user } = useContext(AuthContext);
  const [errors, setErrors] = useState({});
  const [countryCode, setCountryCode] = useState('');

  useEffect(() => {
    if (user?.address) {
      const code = getCountryCodeByRegion(user.address);
      setCountryCode(code || '+91');
    } else {
      setCountryCode('+91');
    }
  }, [user?.address]);

  const validateField = useCallback(
    (question, value, allNameValues = {}, pathPrefix = '') => {
      const hasValue = value !== undefined && value !== null && value !== '' && (!Array.isArray(value) || value.length > 0);

      // 1. Required Check
      if (question.is_required && !hasValue) {
        return { message: 'This field is required.', type: 'required' };
      }

      if (!hasValue) return null;

      // 2. Type-specific simple validations
      switch (question.type?.toUpperCase()) {
        case 'EMAIL':
          if (!validateEmail(value)) return { message: 'Invalid email.', type: 'constraint' };
          break;
        case 'TEL':
        case 'PHONENUMBER':
          if (value.replace(/\D/g, '').length > (countryCode || '').replace(/\D/g, '').length && !validatePhone(value)) {
            return { message: 'Invalid phone number.', type: 'constraint' };
          }
          break;
        case 'NUMBER':
        case 'INTEGER':
        case 'DECIMAL':
          if (!validateNumber(value)) return { message: 'Invalid number.', type: 'constraint' };
          break;
        case 'URL':
          if (!validateUrl(value)) return { message: 'Invalid URL format.', type: 'constraint' };
          break;
      }

      // 3. Dynamic constraints and validations (interpolated)
      const allValidations = [...(question.validations || []), ...(question.constraints || [])];

      if (allValidations.length > 0) {
        for (const v of allValidations) {
          const { value: rawRuleVal, error_message: errorMsg } = v;
          const rule = (v.rule || '').toLowerCase();

          // Interpolate rule value (it might reference other fields like min="${field_a}")
          const ruleVal = rawRuleVal !== undefined && rawRuleVal !== null ? interpolateLabel(String(rawRuleVal), allNameValues, pathPrefix) : rawRuleVal;

          let isError = false;
          let defaultMsg = '';

          switch (rule) {
            case 'required':
              if (!hasValue) {
                isError = true;
                defaultMsg = 'This field is required';
              }
              break;
            case 'min':
              if (hasValue && Number(value) < Number(ruleVal)) {
                isError = true;
                defaultMsg = `Value must be at least ${ruleVal}`;
              }
              break;
            case 'max':
              if (hasValue && Number(value) > Number(ruleVal)) {
                isError = true;
                defaultMsg = `Value must be at most ${ruleVal}`;
              }
              break;
            case 'gt':
              if (hasValue && Number(value) <= Number(ruleVal)) {
                isError = true;
                defaultMsg = `Value must be greater than ${ruleVal}`;
              }
              break;
            case 'lt':
              if (hasValue && Number(value) >= Number(ruleVal)) {
                isError = true;
                defaultMsg = `Value must be less than ${ruleVal}`;
              }
              break;
            case 'equals':
            case 'equal':
            case 'eq':
            case 'equalto':
            case 'equal_to':
              if (hasValue) {
                const numVal = Number(value);
                const numRule = Number(ruleVal);
                const isNumericRes = !isNaN(numVal) && !isNaN(numRule) && String(ruleVal).trim() !== '' && String(value).trim() !== '';

                if (isNumericRes) {
                  if (numVal !== numRule) {
                    isError = true;
                    defaultMsg = `Value must be exactly ${ruleVal}`;
                  }
                } else if (String(value) !== String(ruleVal)) {
                  isError = true;
                  defaultMsg = `Value must be exactly ${ruleVal}`;
                }
              }
              break;
            case 'not_equals':
            case 'not_equal':
            case 'neq':
            case 'notequal':
            case 'not_equal_to':
            case 'notequalto':
              if (hasValue) {
                const numVal = Number(value);
                const numRule = Number(ruleVal);
                const isNumericRes = !isNaN(numVal) && !isNaN(numRule) && String(ruleVal).trim() !== '' && String(value).trim() !== '';

                if (isNumericRes) {
                  if (numVal === numRule) {
                    isError = true;
                    defaultMsg = `Value must not be ${ruleVal}`;
                  }
                } else if (String(value) === String(ruleVal)) {
                  isError = true;
                  defaultMsg = `Value must not be ${ruleVal}`;
                }
              }
              break;
            case 'numeric':
              if (hasValue && !/^-?\d*\.?\d+$/.test(String(value))) {
                isError = true;
                defaultMsg = 'Must be a valid number';
              }
              break;
            case 'regex':
              try {
                if (hasValue && !new RegExp(ruleVal).test(String(value))) {
                  isError = true;
                  defaultMsg = 'Invalid format';
                }
              } catch {
                // Ignore invalid regex
              }
              break;
            case 'min_length':
              if (hasValue && String(value).length < Number(ruleVal)) {
                isError = true;
                defaultMsg = `Minimum ${ruleVal} characters required`;
              }
              break;
            case 'max_length':
              if (hasValue && String(value).length > Number(ruleVal)) {
                isError = true;
                defaultMsg = `Maximum ${ruleVal} characters allowed`;
              }
              break;
          }

          if (isError) {
            return {
              message: errorMsg || defaultMsg || `Validation failed: ${rule}`,
              type: rule === 'required' ? 'required' : 'constraint'
            };
          }
        }
      }

      return null;
    },
    [countryCode]
  );

  useEffect(() => {
    if (!survey || (!survey.questions && !survey.sections)) {
      setErrors({});
      return;
    }

    const newErrors = {};

    const checkFields = (sections, pathPrefix = '') => {
      sections?.forEach(section => {
        // Skip validation for sections hidden by relevance
        if (section.relevance && !checkRelevance(section, nameValues, pathPrefix)) return;

        if (section.is_repeat) {
          const key = pathPrefix ? `${pathPrefix}${section.name}` : section.name;
          const count = activeRepeats[key] || 0;
          for (let i = 0; i < count; i++) {
            const itemPath = `${pathPrefix}${section.name}_${i}_`;
            (section.fields || []).forEach(question => {
              if (question.type?.toLowerCase() === 'note') return;
              if (question.relevance && !checkRelevance(question, nameValues, itemPath)) return;

              const name = `${itemPath}${question.name}`;
              const value = formValues[name];
              const error = validateField(question, value, nameValues, itemPath);
              if (error) {
                newErrors[name] = error;
              }
            });
            if (section.sections) checkFields(section.sections, itemPath);
          }
        } else {
          (section.fields || []).forEach(question => {
            if (question.type?.toLowerCase() === 'note') return;
            if (question.relevance && !checkRelevance(question, nameValues, pathPrefix)) return;

            const name = pathPrefix ? pathPrefix + question.name : question.name;
            const value = formValues[name];
            const error = validateField(question, value, nameValues, pathPrefix);
            if (error) {
              newErrors[name] = error;
            }
          });
          if (section.sections) checkFields(section.sections, pathPrefix);
        }
      });
    };

    if (survey?.sections) {
      checkFields(survey.sections);
    } else if (survey?.questions) {
      survey.questions.forEach(question => {
        if (question.type?.toLowerCase() === 'note') return;
        if (question.relevance && !checkRelevance(question, nameValues)) return;
        const value = formValues[question.name];
        const error = validateField(question, value, nameValues);
        if (error) {
          newErrors[question.name] = error;
        }
      });
    }

    setErrors(newErrors);
  }, [formValues, survey, countryCode, nameValues, activeRepeats, validateField]);

  return { errors, countryCode };
};
